import { useEffect, useMemo, useState, type FC } from "react";
import dayjs from "dayjs";
import { CreditCardIcon, Trash2Icon } from "lucide-react";
import { QueryState } from "@/ds/custom/QueryState";
import { TableCell } from "@/ds/shadcn/components/ui/table";
import { Button } from "@/ds/shadcn/components/ui/button";
import { DropdownMenuGroup, DropdownMenuItem } from "@/ds/shadcn/components/ui/dropdown-menu";
import type { ProblemResponse } from "@/lib/http";
import { useTitle } from "@/lib/common/useTitle";
import { getPaymentCardProviderImage } from "@/lib/media/paymentCardProviders";
import { usePaymentCards } from "@/features/paymentCards/api/usePaymentCards";
import { usePaymentCardDelete } from "@/features/paymentCards/api/usePaymentCardDelete";
import { HeaderBar } from "@/features/common/components/HeaderBar";
import { ModalConfirm } from "@/features/common/components/ModalConfirm";
import { DataView } from "@/features/common/components/dataview/DataView";
import { ListItem, ListItemMenu } from "@/features/common/components/dataview/ListItem";
import { HeaderBarTools } from "@/features/common/components/HeaderBarTools";
import { StripeCardEntry } from "@/features/paymentCards/components/StripeCardEntry";
import type { PaymentCardModel } from "@/features/paymentCards/models/PaymentCardModel";
import { SplitScreen, SplitScreenLeftPanel, SplitScreenRightPanel } from "@/features/common/components/SplitScreen";

export const PagePaymentCards: FC = () => {
	useTitle("Payment Cards");

	const breadcrumb = useMemo(() => [
		{ label: "Settings" },
		{ label: "Payment Cards" }
	], []);

	const paymentCardDelete = usePaymentCardDelete();

	const [addPaymentCardPanelVisible, setAddPaymentCardPanelVisible] = useState(false);
	const [paymentCardIdToDelete, setPaymentCardIdToDelete] = useState<string | null>(null);
	const [paymentCardDeleteErrorMessage, setPaymentCardDeleteErrorMessage] = useState<ProblemResponse | null>(null);

	useEffect(() => {
		setPaymentCardDeleteErrorMessage(paymentCardDelete.error);
	}, [paymentCardDelete.error]);

	useEffect(() => {
		if (paymentCardIdToDelete) {
			setPaymentCardDeleteErrorMessage(null);
		}
	}, [paymentCardIdToDelete]);

	const paymentCards = usePaymentCards();

	const onDeleteConfirmed = () => paymentCardIdToDelete && paymentCardDelete.mutate(paymentCardIdToDelete, {
		onSuccess: () => setPaymentCardIdToDelete(null)
	});

	return (
		<>
			<HeaderBar title="Payment Cards" description="Manage your payment methods" breadcrumb={breadcrumb} />
			<HeaderBarTools>
				{
					paymentCards.isSuccess && paymentCards.data.length > 0 && (
						<Button variant="outline" onClick={() => setAddPaymentCardPanelVisible(!addPaymentCardPanelVisible)}>
							<CreditCardIcon /> Add Payment Card
						</Button>
					)
				}
			</HeaderBarTools>

			<ModalConfirm
				title="Delete Payment Card"
				description="Are you sure you want to delete this payment card?"
				confirmText="Delete"
				confirmDestructive
				isLoading={paymentCardDelete.isPending}
				error={paymentCardDeleteErrorMessage}
				open={!!paymentCardIdToDelete}
				onOpenChange={open => !open && setPaymentCardIdToDelete(null)}
				onConfirm={onDeleteConfirmed} />

			<QueryState query={paymentCards}>
				{
					data => data.length === 0
						? <StripeCardEntry onComplete={() => setAddPaymentCardPanelVisible(false)} />
						: (
							<SplitScreen fixed visible={addPaymentCardPanelVisible} onVisibleChange={visible => !visible && setAddPaymentCardPanelVisible(false)}>
								<SplitScreenLeftPanel>
									<DataView
										items={data}
										getKey={x => x.paymentCardId}
										listViewHeaderLabels={[]}
										listComponent={PaymentCardsDataViewListItem}
										additionalProps={{ setPaymentCardIdToDelete: setPaymentCardIdToDelete }}
										galleryMinWidth={192} />
								</SplitScreenLeftPanel>
								<SplitScreenRightPanel>
									<StripeCardEntry onComplete={() => setAddPaymentCardPanelVisible(false)} />
								</SplitScreenRightPanel>
							</SplitScreen>
						)
				}
			</QueryState>
		</>
	);
};

export interface PaymentCardsDataViewListItemProps extends PaymentCardModel {
	setPaymentCardIdToDelete: (id: string) => void;
}

const PaymentCardsDataViewListItem: FC<PaymentCardsDataViewListItemProps> = paymentCard => {
	const {
		paymentCardId,
		last4,
		provider,
		expiry,
		setPaymentCardIdToDelete
	} = paymentCard;

	return (
		<ListItem className={"flex"}>
			<TableCell className="min-w-0 w-full">
				<div className={"flex items-center gap-2"}>
					<img src={getPaymentCardProviderImage(provider)} className="h-8 w-8" />
					<div className="min-w-0 flex-1">
						<div className="type-item truncate">Card ending in {last4}</div>
						<div className="type-meta truncate">{expiry.valueOf() === 0 ? "●●/●●" : dayjs(expiry).format("MM/YY")}</div>
					</div>
				</div>
			</TableCell>
			<TableCell className="flex w-16">
				<ListItemMenu>
					<DropdownMenuGroup>
						<DropdownMenuItem variant="destructive" onClick={() => setPaymentCardIdToDelete(paymentCardId)}>
							<Trash2Icon />
							Delete
						</DropdownMenuItem>
					</DropdownMenuGroup>
				</ListItemMenu>
			</TableCell>
		</ListItem>
	);
};
